import {
  ActionIcon,
  Badge,
  Group,
  Popover,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconClockPlay,
  IconInfoCircle,
  IconLogout,
  IconRefresh,
  IconWallet,
} from '@tabler/icons-react';

import { AUTO_REFRESH_INTERVAL_MS, useLogout, useReadiness, useSession } from '../api/queries';
import { NotesButton } from './NotesButton';

const AUTO_REFRESH_SECONDS = AUTO_REFRESH_INTERVAL_MS / 1000;

interface AppHeaderProps {
  refreshing: boolean;
  autoRefresh: boolean;
  onRefresh: () => void;
  onAutoRefreshChange: (enabled: boolean) => void;
}

export function AppHeader({
  refreshing,
  autoRefresh,
  onRefresh,
  onAutoRefreshChange,
}: AppHeaderProps) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon size={42} radius="md" variant="light">
          <IconWallet size={22} stroke={1.5} />
        </ThemeIcon>
        <div>
          <Title order={3}>ledger</Title>
          <Text size="sm" c="dimmed">
            Операции, баланс, платежи, хотелки и долги — только просмотр
          </Text>
        </div>
      </Group>

      <Group gap="xs" wrap="nowrap">
        <ReadinessBadge />
        <ReadOnlyHint />
        <NotesButton refreshInterval={autoRefresh ? AUTO_REFRESH_INTERVAL_MS : false} />
        <AutoRefreshToggle enabled={autoRefresh} onChange={onAutoRefreshChange} />
        <Tooltip label="Обновить данные" withArrow>
          <ActionIcon
            variant="default"
            size="lg"
            aria-label="Обновить данные"
            loading={refreshing}
            onClick={onRefresh}
          >
            <IconRefresh size={18} stroke={1.5} />
          </ActionIcon>
        </Tooltip>
        <SessionControls />
      </Group>
    </Group>
  );
}

/** Absent altogether where the deployment asks for no login. */
function SessionControls() {
  const { data: session } = useSession();
  const { mutate: logOut, isPending } = useLogout();

  if (session === undefined || !session.required || session.login === null) {
    return null;
  }

  const label = `Выйти из аккаунта ${session.login}`;
  return (
    <>
      <Text size="sm" c="dimmed" visibleFrom="sm">
        {session.login}
      </Text>
      <Tooltip label={label} withArrow>
        <ActionIcon
          variant="default"
          size="lg"
          aria-label={label}
          loading={isPending}
          onClick={() => {
            logOut();
          }}
        >
          <IconLogout size={18} stroke={1.5} />
        </ActionIcon>
      </Tooltip>
    </>
  );
}

/** Operations are written elsewhere, so the list can go stale without warning. */
function AutoRefreshToggle({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}) {
  const label = enabled
    ? `Автообновление включено: каждые ${AUTO_REFRESH_SECONDS} с`
    : 'Включить автообновление';

  const appearance = enabled
    ? ({ variant: 'light', color: 'brand' } as const)
    : ({ variant: 'default' } as const);

  return (
    <Tooltip label={label} withArrow>
      <ActionIcon
        {...appearance}
        size="lg"
        aria-label={label}
        aria-pressed={enabled}
        onClick={() => onChange(!enabled)}
      >
        <IconClockPlay size={18} stroke={1.5} />
      </ActionIcon>
    </Tooltip>
  );
}

function ReadinessBadge() {
  const { data, isPending } = useReadiness();

  if (isPending) {
    return (
      <Badge color="gray" variant="light" visibleFrom="xs">
        проверка
      </Badge>
    );
  }

  const healthy = data?.status === 'ok';
  return (
    <Tooltip label={`База данных: ${data?.database ?? 'неизвестно'}`} withArrow>
      <Badge color={healthy ? 'teal' : 'red'} variant="light" visibleFrom="xs">
        {healthy ? 'сервис доступен' : 'сервис недоступен'}
      </Badge>
    </Tooltip>
  );
}

function ReadOnlyHint() {
  return (
    <Popover width={320} position="bottom-end" withArrow shadow="md">
      <Popover.Target>
        <ActionIcon variant="subtle" color="gray" size="lg" aria-label="Как менять данные">
          <IconInfoCircle size={18} stroke={1.5} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Интерфейс только для чтения
          </Text>
          <Text size="sm" c="dimmed">
            Записать операцию, добавить хотелку или переставить ей приоритет отсюда нельзя: у HTTP
            API нет таких ручек. Изменения выполняются через MCP-инструменты, которые дёргают те же
            сценарии сервиса.
          </Text>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
